import './FeedbackWindow.css';

import { FeedbackService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Feedback } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, ExpandableSection, Flashbar, Form, Grid, Modal, Textarea } from '@amzn/awsui-components-react';
import { useState } from 'react';
import { useIsMounted } from 'react-tidy';

import { feedbackWindowConstants } from '../../../common/Localization';
import happyImage from './images/happy.png';
import mediumImage from './images/medium.png';
import sadImage from './images/sad.png';

interface IFlashMessage {
    message: string,
    type: any
 }

type FeedbackWindowProps = FlashContextInterface & {
    appContext?: Record<string, any>;
    appId: string;
    closeModal?: () => void;
    createdBy: string;
    header?: string;
    orientation?: string;
    srcApp: string;
};

const feedbackService = new FeedbackService();

function FeedbackWindow(props: FeedbackWindowProps): JSX.Element | null {
    const { appId, closeModal, createdBy, header, orientation, flash } = props;
    const [ comment, setComment ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const [ rating, setRating ] = useState<number>(0);
    const flashValue : IFlashMessage = { message: feedbackWindowConstants.FLASH_INFO, type: 'info' };
    const [ flashMessage, setFlashMessage ] = useState(flashValue);
    const [ showFlashBar, setShowFlashBar ] = useState(false);
    const ratingSystem = '3-scale';
    const isMounted = useIsMounted();
    
    const updateComment = (event: any) => {
        const updatedComment = event.detail.value;
        setComment(updatedComment);
    };

    const updateFlashMessage = (message: string, type: string) => {
        flashValue.message = message;
        flashValue.type = type;
        setFlashMessage(flashValue);
    };

    const clearFeedback = () => {
        setComment('');
        setLoading(false);
        setRating(0);
        if (closeModal) {
            closeModal();
        }
    };

    const submitFeedback = async () => {
        setLoading(true);
        setShowFlashBar(true);

        const feedbackToSubmit: Feedback = {
            comment,
            createdBy,
            rating,
            ratingSystem,
            srcApp: appId,
            srcURL: window?.location?.href,
        };

        try {
            await feedbackService.submitFeedback(feedbackToSubmit);
            if (isMounted()) {
                flash.alert(FlashType.success, feedbackWindowConstants.SUCCESS_MESSAGE, feedbackWindowConstants.FLASH_SUCCESS_TITLE);
                clearFeedback();
            }
        } catch (err) {
            if (isMounted()) {
                updateFlashMessage(feedbackWindowConstants.FLASH_ERROR + feedbackWindowConstants.CONTACT_EMAIL, FlashType.error);
            }
            console.log(err);
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    const selectRating = (id: string) => () => {
        let newRating = 0;
        if (id === 'Positive') {
            newRating = 3;
        } else if (id === 'Neutral') {
            newRating = 2;
        } else if (id === 'Negative') {
            newRating = 1;
        }
        setRating(newRating);
    };

    const horizontalOrientation: boolean = orientation && orientation === 'h' ? true : false;
    const selectedRating3: string = rating === 3 ? 'selected-rating' : '';
    const selectedRating2: string = rating === 2 ? 'selected-rating' : '';
    const selectedRating1: string = rating === 1 ? 'selected-rating' : '';
    const headerText: string = header ? header : feedbackWindowConstants.HEADER;
    const comments = (
        <ExpandableSection header={feedbackWindowConstants.COMMENT} defaultExpanded={!horizontalOrientation}>
            <Textarea
                placeholder={feedbackWindowConstants.COMMENT_PLACEHOLDER}
                value={comment}
                disabled={loading}
                onChange={updateComment}
                data-testid='textarea-description'
            />
        </ExpandableSection>
    );
    const horizontalComment = horizontalOrientation ? (
        <div>
            {comments}
        </div>
    ) : ('');
    const verticalComment = !horizontalOrientation ? comments : ('');

    const ratingControls = (
        <Grid disableGutters gridDefinition={[{colspan: 4}, {colspan: 4}, {colspan: 4}, {colspan: 4}, {colspan: 4}, {colspan: 4}]}>
            <Box textAlign='center' className={selectedRating3}>
                <Button id='Positive' onClick={selectRating('Positive')} variant='link' data-testid='btn-positive' >
                    <img className='image-button' src={happyImage} data-testid='img-positive' />
                </Button>
            </Box>
            <Box textAlign='center' className={selectedRating2}>
                <Button id='Neutral' onClick={selectRating('Neutral')} variant='link' data-testid='btn-neutral' >
                    <img className='image-button' src={mediumImage} data-testid='img-neutral' />
                </Button>
            </Box>
            <Box textAlign='center' className={selectedRating1}>
                <Button id='Negative' onClick={selectRating('Negative')} variant='link' data-testid='btn-negative' >
                    <img className='image-button' src={sadImage} data-testid='img-negative' />
                </Button>
            </Box>
            <Box textAlign='center'>
                <Button wrapText={false} id='Positive' onClick={selectRating('Positive')} variant='link'><p>{feedbackWindowConstants.POSITIVE}</p></Button>
            </Box>
            <Box textAlign='center'>
                <Button wrapText={false} id='Neutral' onClick={selectRating('Neutral')} variant='link'><p>{feedbackWindowConstants.NEUTRAL}</p></Button>
            </Box>
            <Box textAlign='center'>
                <Button wrapText={false} id='Negative' onClick={selectRating('Negative')} variant='link'><p>{feedbackWindowConstants.NEGATIVE}</p></Button>
            </Box>
        </Grid>
    );

    const gridDefinition = horizontalOrientation ? [{colspan: 6}, {colspan: 6}] : [{colspan: 12}, {colspan: 12}];
    const formActions = (
        <div>
            <Button variant='link' data-testid='btn-cancel' onClick={closeModal}>{feedbackWindowConstants.CANCEL}</Button>
            <Button variant='primary' data-testid='btn-submit' onClick={submitFeedback} disabled={rating === 0} loading={loading} >
                {feedbackWindowConstants.SUBMIT}
            </Button>
        </div>
    );

    return (
        <div className='form-action'>
            <Modal header={headerText} onDismiss={closeModal} visible>
                <Form actions={formActions}>
                    <Grid gridDefinition={gridDefinition}>
                        {ratingControls}
                        {horizontalOrientation ? horizontalComment : verticalComment}
                    </Grid>
                    {showFlashBar?
                        (
                            <Flashbar  
                                items={[ 
                                    {
                                        content: flashMessage.message, 
                                        type: flashMessage.type,
                                        dismissible: true,
                                        onDismiss: () => setShowFlashBar(false)
                                    }
                                ]} 
                            />
                        )
                        :null
                    }
                </Form>
            </Modal>
        </div>
    );
}

export default withFlashContext(FeedbackWindow);
